import { createRoot } from "react-dom/client";
import { GeolocationProvider } from "./contexts/GeolocationContext";
import { GlobalErrorHandler, setupGlobalErrorHandlers } from "./components/GlobalErrorHandler";
import App from "./App.tsx";
import "./index.css";
import "./lib/i18n"; // Import i18n synchronously (non-blocking)
import { preloadCriticalData } from "./services/preloadService";
import { registerServiceWorker } from "./registerServiceWorker";
import { setupChunkErrorHandler } from "./utils/chunkErrorHandler";
import { patchSupabaseGetUser } from "./lib/patchSupabaseAuth";
import { silenceConsoleInProduction } from "./lib/silenceConsole";

// Silence noisy console.* calls in production builds.
silenceConsoleInProduction();

// Patch supabase.auth.getUser() to read from cached session (no network).
// Must run before any feature code calls getUser(). See file for rationale.
patchSupabaseGetUser();

// Setup chunk error handler first to catch loading failures
setupChunkErrorHandler();

// Setup global error handlers
setupGlobalErrorHandlers();

// Render immediately - don't block on i18n initialization
const root = createRoot(document.getElementById("root")!);
root.render(
  <GlobalErrorHandler>
    <GeolocationProvider>
      <App />
    </GeolocationProvider>
  </GlobalErrorHandler>
);

// Defer non-critical bootstrap work to idle time so it never competes
// with the React mount / first interaction (improves Max Potential FID).
const scheduleIdle =
  typeof window !== "undefined" && "requestIdleCallback" in window
    ? (cb: () => void) => (window as any).requestIdleCallback(cb, { timeout: 2000 })
    : (cb: () => void) => setTimeout(cb, 200);

scheduleIdle(() => {
  // Preload critical course data in the background (network + cache writes
  // moved off the critical path)
  preloadCriticalData();
});

scheduleIdle(() => {
  registerServiceWorker();
});
